import { type ReactNode, useMemo } from 'react'
import {
  type LocationId,
  type CustomFieldCalculator,
  type CustomFieldData,
  type CustomFieldValue,
} from '@terros/common'
import { DateTimePicker } from '@mantine/dates'
import { TextInput, NumberInput, Select, type InputWrapperProps } from '@mantine/core'
import { toCustomFieldSelectOptions } from '@front/model'
import { AttachmentInput } from '../attachment'
import { type InputFieldProps } from './types'
import { MultipleChoiceMultiSelect } from './MultipleChoiceMultiSelect'
import { toDateCustomFieldValue, toNumberCustomFieldValue } from './inputValue'

type CustomFieldInputProps = {
  field: CustomFieldData
  onChange: (value: CustomFieldValue) => void
  calculator: CustomFieldCalculator
  locationId?: LocationId
  disabled?: boolean
} & Pick<InputWrapperProps, 'error' | 'withAsterisk'>

export const CustomFieldInput = ({
  field,
  onChange,
  calculator,
  locationId,
  disabled,
  ...inputWrapperProps
}: CustomFieldInputProps): ReactNode => {
  const data = useMemo(() => calculator.getValueAndType(field.fieldId) || {}, [calculator, field.fieldId])
  const { type, value } = data
  const { format } = field

  const commonProps: InputFieldProps = {
    ...inputWrapperProps,
    label: field.label,
    description: field.description,
  }

  switch (type) {
    case 'string':
      return <TextInput {...commonProps} disabled={disabled} value={value} onChange={(e) => onChange(e.target.value)} />
    case 'url':
      return (
        <TextInput
          {...commonProps}
          disabled={disabled}
          type='url'
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )
    case 'boolean':
      return (
        <Select
          {...commonProps}
          clearable
          data={[
            { label: 'True', value: 'true' },
            { label: 'False', value: 'false' },
          ]}
          disabled={disabled}
          placeholder='Unknown'
          value={value?.toString() ?? null}
          onChange={(value) => onChange(value === null ? null : value === 'true')}
        />
      )
    case 'date':
      return (
        <DateTimePicker
          {...commonProps}
          clearable
          disabled={disabled}
          value={value ?? null}
          valueFormat='MMM DD, YYYY, hh:mm A'
          onChange={(value) => onChange(toDateCustomFieldValue(value))}
        />
      )
    case 'number':
      return (
        <NumberInput
          {...commonProps}
          disabled={disabled}
          value={value}
          onChange={(value) => onChange(toNumberCustomFieldValue(value))}
          thousandSeparator={format?.delimiter}
          decimalScale={format?.decimalPlaces}
          prefix={format?.prefix}
          suffix={format?.suffix}
        />
      )
    case 'calculation':
      return (
        <NumberInput
          {...commonProps}
          disabled
          thousandSeparator={format?.delimiter}
          decimalScale={format?.decimalPlaces}
          prefix={format?.prefix}
          suffix={format?.suffix}
          value={value}
        />
      )
    case 'multipleChoice':
      if (field.allowMultiple)
        return (
          <MultipleChoiceMultiSelect
            {...commonProps}
            disabled={disabled}
            options={data.options}
            value={calculator.findCustomFieldValue(field.fieldId)}
            onChange={onChange}
          />
        )
      return (
        <Select
          clearable
          data={toCustomFieldSelectOptions(data.options, value)}
          {...commonProps}
          disabled={disabled}
          value={value}
          onChange={onChange}
        />
      )
    case 'attachment':
      return (
        <AttachmentInput
          {...commonProps}
          disabled={disabled}
          value={value}
          onChange={onChange}
          locationId={locationId}
        />
      )
  }
}
